import { Pallet_t, PalletConfigObj, Stack } from 'blackbox';
import { useState, useContext, useEffect } from 'react';
import { Scroll } from '../General';
import { KeyboardTrigger, KeyboardContext } from '../Layout';
import { ItemFilter } from '../pallet_config/Shared';
import Button from '../PendantButton';
import { COLOR } from '../shared/shared';
import StackScene from '../three/StackScene';
import { lBlueFilter } from './OperationPage';
import close from '../../images/close.png';

interface DialogHeaderProps {
	title: string;
	searchFilter: string;
	updateSearchFilter: (filter: string) => void;
	selection: BaySelection;
	setSelection: (selection: BaySelection) => void;
	clearSelection: () => void;
	endEditing: () => void;
	pallets: Pallet_t[];
}

function DialogHeader({
	title,
	searchFilter,
	updateSearchFilter,
	selection,
	setSelection,
	clearSelection,
	endEditing,
	pallets,
}: DialogHeaderProps) {
	const keyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	return (
		<>
			<div className="background-silver full-width rounded-top padding-bottom flex-row flex-between align-center">
				<div className="full-height flex-row align-center double-padding-top">
					<h1 className="font-6 bold-font color-blue double-padding-left">
						{'Edit ' + title}
					</h1>
				</div>

				<div className="full-height flex-row align-center double-padding-top">
					<ItemFilter
						pSearchFilter={searchFilter}
						pSetSearchFilter={filter => updateSearchFilter(filter.toLowerCase())}
						triggerKeyboard={keyboardTrigger}
					/>
					{searchFilter.length > 0 && (
						<Button pAction={() => updateSearchFilter('')} className="margin-left">
							<span>Clear</span>
						</Button>
					)}
				</div>
				<div className="full-height flex-row align-center double-padding-top">
					<Button
						pDisabled={selection.stack < 0}
						pAction={() => {
							clearSelection();
						}}
						pNormalColorOverride={
							selection.stack < 0 ? COLOR.DARK_GREY : COLOR.MEDIUM_BLUE
						}
						className="double-margin-right"
					>
						<span className="font-5 bold-font padding color-white">
							Clear Selection
						</span>
					</Button>
					<Button
						pAction={endEditing}
						pNormalColorOverride={COLOR.TRANSPARENT}
						className="height-60 double-margin-right"
					>
						<img
							className="full-height auto-width"
							style={{ filter: lBlueFilter }}
							src={close}
							alt="close"
						/>
					</Button>
				</div>
			</div>
			<div className="double-margin-top flex-row align-center">
				{/* filters here */}
				<span
					className="font-5 double-padding-left double-margin-right"
					style={{ whiteSpace: 'nowrap' }}
				>
					Filter Pallet:
				</span>
				<Scroll className="full-height flex-row align-center" horizontal={true}>
					{pallets.map((pallet, index) => (
						<div
							key={index}
							className={
								'round margin-left padding ' +
								(selection.pallet === index
									? 'background-transparent-green border-green'
									: 'border')
							}
							onClick={() => {
								setSelection({ stack: -1, pallet: index });
							}}
						>
							<span
								className={
									'double-padding font-5 ' +
									(selection.pallet === index ? 'color-green' : '')
								}
								style={{ whiteSpace: 'nowrap' }}
							>
								{pallet.name}
							</span>
						</div>
					))}
					<div className="padding"></div>
				</Scroll>
			</div>
		</>
	);
}

export interface BaySelection {
	stack: number;
	pallet: number;
}

interface BayEditDialogProps {
	title: string;
	palletConfig: PalletConfigObj;
	initialIndex: number;
	initialPalletIndex: number;
	updateQueue: (pPalletID: number, pStackID: number) => void;
	endEditing: () => void;
	clearSelection: () => void;
	bay: number;
	updateLeft: (selection: BaySelection | null) => void;
	updateRight: (selection: BaySelection | null) => void;
}

export function BayEditDialog({
	title,
	palletConfig,
	initialIndex,
	initialPalletIndex,
	updateQueue,
	endEditing,
	clearSelection,
	bay,
	updateLeft,
	updateRight,
}: BayEditDialogProps) {
	const pallets = palletConfig.config.pallets;
	const [selection, setSelection] = useState<BaySelection>({
		stack: initialIndex,
		pallet: initialPalletIndex,
	});
	const [saveDisabled, setSaveDisabled] = useState(true);

	useEffect(() => {
		setSaveDisabled(selection.pallet < 0 || selection.stack < 0);
	}, [selection]);

	const [searchFilter, setSearchFilter] = useState('');
	const updateSearchFilter = function (filter: string) {
		setSearchFilter(filter.toLowerCase());
	};

	return (
		<div className="full-size flex-col">
			<DialogHeader
				title={title}
				searchFilter={searchFilter}
				updateSearchFilter={updateSearchFilter}
				selection={selection}
				setSelection={setSelection}
				endEditing={endEditing}
				pallets={palletConfig.config.pallets}
				clearSelection={() => {
					clearSelection();
					if (bay === 0) updateLeft(null);
					else if (bay === 1) updateRight(null);
				}}
			/>
			<div className="flex-row flex-around height-78 full-width double-margin-top">
				<div className="container-border width-30 full-height">
					<Scroll className="padding">
						<div className="flex-col align-center">
							{pallets[selection.pallet].Stack.length > 0 &&
								pallets[selection.pallet].Stack.map(
									(stack: Stack, stackIndex: number) => {
										const isSelected = selection.stack === stackIndex;

										if (searchFilter.length > 0) {
											const lRegExp = new RegExp(searchFilter);
											if (!lRegExp.test(stack.name.toLowerCase())) {
												return void 0;
											}
										}
										return (
											<div
												key={stackIndex}
												className={
													'full-width background-silver rounded margin-bottom double-padding-top double-padding-bottom ' +
													`${isSelected ? 'blue-inner-border' : ''}`
												}
												onClick={() => {
													if (!isSelected)
														setSelection(prev => ({ ...prev, stack: stackIndex }));
												}}
											>
												<span
													className={
														'font-5 margin-left color-blue ' +
														`${isSelected ? 'bold-font ' : ''}`
													}
												>
													{stack.name}
												</span>
											</div>
										);
									}
								)}
							{pallets[selection.pallet].Stack.length === 0 && (
								<span className="font-5 margin-top">
									No stacks available <br />
									for selected pallet
								</span>
							)}
						</div>
					</Scroll>
				</div>
				<div className="container-border width-66 grid-row-90">
					<div className="full-width full-height">
						{pallets[selection.pallet].Stack.length > 0 && selection.stack >= 0 && (
							<>
								<StackScene
									palletConfig={palletConfig}
									stackIndex={selection.stack}
									palletIndex={selection.pallet}
									bayIndex={bay}
									useHighlights={false}
									zoom={3}
								/>
							</>
						)}
					</div>
					<div className="full-size flex-row flex-between align-end">
						<Button pAction={endEditing} pClass="margin-bottom margin-left">
							<span className="font-5 bold-font vw-5">Cancel</span>
						</Button>
						<Button
							pAction={() => {
								// Update queue
								updateQueue(selection.pallet, selection.stack);
								if (bay === 0) updateLeft(selection);
								else if (bay === 1) updateRight(selection);
								endEditing();
							}}
							pNormalColorOverride={saveDisabled ? COLOR.DARK_GREY : COLOR.GREEN}
							pDisabled={saveDisabled}
							pClass="margin-bottom margin-right"
						>
							<span className="font-5 bold-font vw-5">Save</span>
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
